/**
 * Reprogramación de CITOFONÍA en la central (S21).
 *
 * Pantalla SEPARADA de Suspensiones a propósito: distinto origen del cambio.
 * - Suspensiones = corte/reconexión por MOROSIDAD.
 * - Reprogramación = un vecino cambió su celular (ya validado por la
 *   Comisión) y el dato programado en la central quedó desfasado; el operador
 *   debe actualizarlo de `valor_actual` → `valor_nuevo`.
 *
 * Solo CITOFONÍA. El acceso wifi salió de acá con la integración eWeLink
 * (2026-09-02, convención #17): dejó de ser espejo del hardware porque el
 * sistema lo opera por API, y su worklist vive en `/tesoreria/acceso-wifi`. La
 * citofonía se queda porque hay que ir a teclear en la central a mano.
 *
 * Doctrina espejo del HW (vigente para citofonía): el sistema solo registra el
 * estado. "Marcar hecho" no toca hardware; declara que el operador ya dejó la
 * central al día. Envía el `tipo_trabajo` que mostraba la lista: si la casa
 * cambió de estado entremedio, el backend responde 409 y la lista se recarga.
 *
 * Presentación (feedback del tesorero): tablas ordenadas por casa y slot —
 * "Nuevo — programar" (altas), "Reactivación" (casa que se reconecta),
 * "Actualización" (dato cambió, muestra actual → nuevo), "Eliminar de la
 * central" (acceso dado de baja que seguía programado) y "Suspensión —
 * desprogramar" (casa que se corta, 2026-10-07) — en vez de tarjetas sueltas.
 *
 * Mismo patrón de doble vista que Suspensiones: la Comisión LEE
 * (`/admin/reprogramacion`), el tesorero OPERA (`/tesoreria/reprogramacion`).
 *
 * 2026-10-06 — se suma "Llaveros y controles por desprogramar"
 * (`SeccionPorDesprogramar`): recursos dados de baja que hay que borrar a mano
 * de motores y puertas. Carga y errores propios, para que una falla de una
 * lista no esconda la otra.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { listarReprogramaciones, marcarReprogramado } from '../../lib/api/suspensiones-lote';
import {
  listarPorDesprogramar,
  marcarDesprogramado,
} from '../../lib/api/recursos';
import { ApiError } from '../../lib/api/client';
import type {
  RecursoPorDesprogramarRead,
  ReprogramacionPendienteRead,
} from '../../lib/api/types';
import { SeccionPorDesprogramar } from '../../components/tesoreria/SeccionPorDesprogramar';
import { useAuth } from '../../lib/auth/AuthContext';
import { ROLES_REPROGRAMAN, tieneAlgunRol } from '../../lib/auth/roles';
import { vistaActualPorPath } from '../../lib/layout/vista';
import { formatCelular } from '../../lib/format';
import { nombreConParentesco } from '../../lib/labels';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';

/** Etiqueta de recurso para la columna. */
const RECURSO_LABEL: Record<string, string> = {
  citofonia: 'Citofonía',
};

/** Clave estable de un acceso (identifica la fila para el estado "registrado"
 *  optimista, sin recargar la lista). */
export function accesoKey(r: ReprogramacionPendienteRead): string {
  return `${r.tipo}-${r.acceso_id}`;
}

/** Valor formateado. Solo llegan celulares (la pantalla es de citofonía),
 *  pero se respeta el tipo por si el backend suma otro. */
function valorReprog(
  r: ReprogramacionPendienteRead,
  val: string | null,
): string {
  if (!val) return '—';
  return r.tipo === 'citofonia' ? formatCelular(val) : val;
}

/** Orden estable: por casa, luego tipo, luego slot (los sin slot al final).
 *  El orden por tipo quedó de cuando esta pantalla mezclaba citofonía y wifi;
 *  se conserva para no romper si el backend suma otro tipo. */
const ORDEN_TIPO: Record<string, number> = { citofonia: 0 };
function ordenar(
  items: ReprogramacionPendienteRead[],
): ReprogramacionPendienteRead[] {
  return [...items].sort(
    (a, b) =>
      a.lote_identificador.localeCompare(b.lote_identificador) ||
      (ORDEN_TIPO[a.tipo] ?? 9) - (ORDEN_TIPO[b.tipo] ?? 9) ||
      (a.slot ?? 99) - (b.slot ?? 99),
  );
}

/** Una sección = título + tabla ordenada. Se oculta si no tiene filas.
 *  `modo` decide la columna de valor: `programar` muestra el valor a dar de
 *  alta; `reprogramar` muestra "actual → nuevo"; `eliminar` muestra el valor a
 *  quitar del HW (en rojo). */
function SeccionReprogramaciones({
  titulo,
  descripcion,
  items,
  modo,
  puedeOperar,
  onMarcar,
  registrados,
  procesando,
}: {
  titulo: string;
  descripcion: string;
  items: ReprogramacionPendienteRead[];
  modo: 'programar' | 'reprogramar' | 'eliminar';
  puedeOperar: boolean;
  onMarcar: (r: ReprogramacionPendienteRead) => void;
  registrados: Set<string>;
  procesando: string | null;
}) {
  if (items.length === 0) return null;
  const filas = ordenar(items);
  return (
    <section className="bg-bg-elevated rounded-lg shadow overflow-hidden">
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-bold uppercase tracking-wide text-text-primary">
          {titulo}{' '}
          <span className="text-text-secondary font-normal normal-case">
            ({items.length})
          </span>
        </h3>
        <p className="text-xs text-text-secondary mt-0.5">{descripcion}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-text-muted border-y border-line">
              <th className="px-4 py-2 font-medium">Casa</th>
              <th className="px-3 py-2 font-medium">Residente</th>
              <th className="px-3 py-2 font-medium">Recurso</th>
              <th className="px-3 py-2 font-medium text-center">Slot</th>
              <th className="px-3 py-2 font-medium">
                {modo === 'programar'
                  ? 'Teléfono'
                  : modo === 'eliminar'
                    ? 'Quitar del HW'
                    : 'Cambio (actual → nuevo)'}
              </th>
              {puedeOperar && <th className="px-4 py-2 font-medium text-right" />}
            </tr>
          </thead>
          <tbody>
            {filas.map((r) => {
              const quien =
                nombreConParentesco(r.residente_nombre, r.parentesco) ?? '—';
              return (
                <tr
                  key={`${r.tipo}-${r.acceso_id}`}
                  className="border-b border-line/60 last:border-0 hover:bg-bg-page/40"
                >
                  <td className="px-4 py-2 font-semibold text-text-primary whitespace-nowrap">
                    {r.lote_identificador}
                  </td>
                  <td className="px-3 py-2 text-text-primary">{quien}</td>
                  <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
                    {RECURSO_LABEL[r.tipo] ?? r.tipo}
                  </td>
                  <td className="px-3 py-2 text-center text-text-secondary">
                    {r.slot ?? '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {modo === 'programar' ? (
                      <span className="font-mono text-orange">
                        {valorReprog(r, r.valor_nuevo)}
                      </span>
                    ) : modo === 'eliminar' ? (
                      <span className="font-mono text-red-400">
                        {valorReprog(r, r.valor_actual)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-mono text-text-secondary">
                          {valorReprog(r, r.valor_actual)}
                        </span>
                        <span className="text-text-muted">→</span>
                        <span className="font-mono text-orange">
                          {valorReprog(r, r.valor_nuevo)}
                        </span>
                      </span>
                    )}
                  </td>
                  {puedeOperar && (
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {registrados.has(accesoKey(r)) ? (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-600 px-2.5 py-1 text-sm font-medium text-white">
                          ✓ Registrado
                        </span>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={procesando === accesoKey(r)}
                          onClick={() => onMarcar(r)}
                        >
                          {procesando === accesoKey(r)
                            ? 'Registrando…'
                            : 'Marcar hecho'}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Lista de accesos a reprogramar, en tablas ordenadas por casa y recurso
 *  (programar / reactivación / actualizar / eliminar / suspensión). Exportada
 *  para reuso fiel en el preview dev-only. */
export function ListaReprogramaciones({
  items,
  puedeOperar,
  onMarcar,
  registrados,
  procesando,
}: {
  items: ReprogramacionPendienteRead[];
  puedeOperar: boolean;
  onMarcar: (r: ReprogramacionPendienteRead) => void;
  registrados: Set<string>;
  procesando: string | null;
}) {
  if (items.length === 0) {
    return (
      <div className="bg-bg-elevated rounded-lg shadow p-5 text-sm text-text-secondary">
        No hay accesos pendientes de programar, actualizar ni eliminar.
      </div>
    );
  }
  const programar = items.filter((r) => r.tipo_trabajo === 'programar');
  const reactivacion = items.filter(
    (r) => r.tipo_trabajo === 'programar_reactivacion',
  );
  const reprogramar = items.filter((r) => r.tipo_trabajo === 'reprogramar');
  const eliminar = items.filter((r) => r.tipo_trabajo === 'eliminar');
  const suspension = items.filter(
    (r) => r.tipo_trabajo === 'desprogramar_suspension',
  );
  return (
    <div className="space-y-6">
      <SeccionReprogramaciones
        titulo="Nuevo — programar"
        descripcion="Altas ya validadas que nunca se han programado en el hardware."
        items={programar}
        modo="programar"
        puedeOperar={puedeOperar}
        onMarcar={onMarcar}
        registrados={registrados}
        procesando={procesando}
      />
      <SeccionReprogramaciones
        titulo="Reactivación — volver a programar"
        descripcion="Casas en proceso de reconexión: vuelve a programar estos celulares en la central y confirma la reconexión en Suspensiones."
        items={reactivacion}
        modo="programar"
        puedeOperar={puedeOperar}
        onMarcar={onMarcar}
        registrados={registrados}
        procesando={procesando}
      />
      <SeccionReprogramaciones
        titulo="Actualización"
        descripcion="Ya estaban programados; el vecino cambió su celular."
        items={reprogramar}
        modo="reprogramar"
        puedeOperar={puedeOperar}
        onMarcar={onMarcar}
        registrados={registrados}
        procesando={procesando}
      />
      <SeccionReprogramaciones
        titulo="Eliminar de la central"
        descripcion="Accesos dados de baja (slot vaciado, residente eliminado o baja del hogar) que seguían programados: hay que quitarlos del hardware."
        items={eliminar}
        modo="eliminar"
        puedeOperar={puedeOperar}
        onMarcar={onMarcar}
        registrados={registrados}
        procesando={procesando}
      />
      <SeccionReprogramaciones
        titulo="Suspensión — desprogramar"
        descripcion="Casas en proceso de corte o suspendidas: quita estos celulares de la central. Al reactivar la casa volverán a aparecer para programarlos."
        items={suspension}
        modo="eliminar"
        puedeOperar={puedeOperar}
        onMarcar={onMarcar}
        registrados={registrados}
        procesando={procesando}
      />
    </div>
  );
}

export function Reprogramacion() {
  const { me } = useAuth();
  const { pathname } = useLocation();
  // Gate por CONTEXTO de ruta (igual que Suspensiones): operar solo desde la
  // vista Tesorería; la vista Comisión es siempre read-only.
  const esVistaTesoreria = vistaActualPorPath(pathname) === 'tesoreria';
  const puedeOperar =
    !!me &&
    esVistaTesoreria &&
    tieneAlgunRol(me.roles_vigentes, ROLES_REPROGRAMAN);

  const [items, setItems] = useState<ReprogramacionPendienteRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Accesos marcados en esta sesión (feedback optimista, sin recargar la lista):
  // la fila queda con "✓ Registrado" verde y desaparece al recargar la página.
  const [registrados, setRegistrados] = useState<Set<string>>(
    () => new Set(),
  );
  const [procesando, setProcesando] = useState<string | null>(null);

  const [porDesprogramar, setPorDesprogramar] = useState<
    RecursoPorDesprogramarRead[]
  >([]);
  const [desprogLoading, setDesprogLoading] = useState(true);
  const [desprogError, setDesprogError] = useState<string | null>(null);
  const [desprogramados, setDesprogramados] = useState<Set<string>>(
    () => new Set(),
  );

  const cargarPorDesprogramar = useCallback(async () => {
    setDesprogLoading(true);
    setDesprogError(null);
    try {
      setPorDesprogramar(await listarPorDesprogramar());
    } catch {
      setDesprogError(
        'No pudimos cargar los llaveros y controles por desprogramar.',
      );
    } finally {
      setDesprogLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarPorDesprogramar();
  }, [cargarPorDesprogramar]);

  async function onMarcarDesprogramado(
    recursoId: string,
    nota: string | null,
  ): Promise<string | null> {
    try {
      await marcarDesprogramado(recursoId, nota);
      setDesprogramados((prev) => new Set(prev).add(recursoId));
      return null;
    } catch (err) {
      // El 409 trae el motivo en español (sigue en uso / ya estaba marcado).
      const detail =
        err instanceof ApiError &&
        err.body &&
        typeof err.body === 'object' &&
        'detail' in err.body
          ? (err.body as { detail: unknown }).detail
          : null;
      return typeof detail === 'string'
        ? detail
        : 'No pudimos registrarlo. Intenta de nuevo.';
    }
  }

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listarReprogramaciones());
    } catch {
      setError('No pudimos cargar las reprogramaciones. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function onMarcar(r: ReprogramacionPendienteRead) {
    const key = accesoKey(r);
    // Feedback inmediato in-place: no recargamos la lista (sería una lata en
    // cada ítem). La fila queda "✓ Registrado" y sale al recargar la página.
    setProcesando(key);
    try {
      await marcarReprogramado(r.tipo, r.acceso_id, r.tipo_trabajo);
      setRegistrados((prev) => new Set(prev).add(key));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // La casa cambió de estado (suspensión/reactivación) desde que se
        // cargó la lista: se recarga para mostrar el trabajo vigente.
        setToast('La lista cambió desde que la abriste. Revisa de nuevo.');
        cargar();
      } else {
        // Error transitorio (no oculta la lista): reintentar.
        setToast('No pudimos registrar el acceso. Intenta de nuevo.');
      }
    } finally {
      setProcesando(null);
    }
  }

  if (!me) return null;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Reprogramación</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          Lo que debes tocar en el hardware: citofonía para programar,
          actualizar o eliminar de la central (incluidas las casas que se
          suspenden o se reactivan), y llaveros o controles dados de baja que
          hay que borrar de los motores y las puertas. Marca cada uno
          como hecho al dejarlo listo en el equipo.
        </p>
        {!esVistaTesoreria && (
          <p className="text-xs text-text-secondary mt-1 inline-flex items-center gap-1">
            <Badge variant="info">Solo lectura</Badge>
            "Marcar hecho" se hace desde la vista Tesorería.
          </p>
        )}
      </div>

      <div className="bg-soft-warning/40 border-l-4 border-state-warning p-4 rounded text-sm text-text-on-warning">
        <p className="font-medium">El sistema no toca la central</p>
        <p className="text-xs mt-1">
          "Marcar hecho" solo registra que tú ya programaste, actualizaste o
          quitaste el celular de la central. El WiFi no está aquí: lo opera el
          sistema en eWeLink.
        </p>
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 bg-bg-elevated text-text-primary border border-line px-4 py-2 rounded shadow-lg text-sm z-50">
          {toast}
        </div>
      )}

      {loading && (
        <div className="bg-bg-elevated rounded-lg shadow p-5 text-sm text-text-secondary">
          Cargando…
        </div>
      )}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded text-sm text-text-primary">
          {error}
        </div>
      )}

      {!loading && !error && (
        <ListaReprogramaciones
          items={items}
          puedeOperar={puedeOperar}
          onMarcar={onMarcar}
          registrados={registrados}
          procesando={procesando}
        />
      )}

      {desprogLoading && (
        <div className="bg-bg-elevated rounded-lg shadow p-5 text-sm text-text-secondary">
          Cargando llaveros y controles…
        </div>
      )}
      {desprogError && !desprogLoading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded text-sm text-text-primary">
          {desprogError}
        </div>
      )}
      {!desprogLoading && !desprogError && (
        <SeccionPorDesprogramar
          items={porDesprogramar}
          puedeOperar={puedeOperar}
          onMarcar={onMarcarDesprogramado}
          registrados={desprogramados}
        />
      )}
    </div>
  );
}
